import _ from 'lodash';
import { createSelector } from 'reselect';

import { AusOperationStatus } from 'constants/loanApplication';
import { formatAusFindingsOptions } from 'utils/aus';

import { selectLoanIdFromDeal } from './deal.selectors';
import { selectLoanApplication } from './loanApplication.selectors';

const selectAusData = createSelector(selectLoanApplication, ({ aus }) => aus);

export const selectAusSubmitOperations = createSelector(selectAusData, ({ submitOperations }) => {
  return submitOperations;
});

export const selectIsAusSubmitting = createSelector(
  selectAusSubmitOperations,
  ausSubmitOperations =>
    Object.values(ausSubmitOperations).some(
      ({ ausOperationStatus }) =>
        ausOperationStatus === AusOperationStatus.NEW ||
        ausOperationStatus === AusOperationStatus.IN_PROGRESS,
    ),
);

// Missing/unparseable timestamps sort oldest so a real createdDate always wins; equal
// timestamps fall back to insertion order via the >= tie-break below.
const toOperationTimestamp = ({ createdDate }) => {
  const time = createdDate ? new Date(createdDate).getTime() : NaN;

  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

// The most recent operation by createdDate — a page reload REPLACES the map in unspecified
// backend-array order, so insertion position is not chronology; a fresh retry (newest
// createdDate) supersedes a stale errored op even when the reload returns them out of order.
// A with-pending-changes run reports into its own tab and never represents the saved loan.
const selectLatestAusSubmitOperation = createSelector(
  selectAusSubmitOperations,
  ausSubmitOperations => {
    const operations = Object.values(ausSubmitOperations).filter(
      ({ isPendingChangesRun }) => !isPendingChangesRun,
    );

    if (!operations.length) {
      return null;
    }

    return operations.reduce((latest, operation) =>
      toOperationTimestamp(operation) >= toOperationTimestamp(latest) ? operation : latest,
    );
  },
);

// The wholesale casefile-intake error state: non-null only when the most recent operation
// ended in ERROR, carrying the backend's aggregated actionable message.
export const selectAusSubmitError = createSelector(selectLatestAusSubmitOperation, operation =>
  operation?.ausOperationStatus === AusOperationStatus.ERROR
    ? { message: operation.error ?? null }
    : null,
);

export const selectAusSummary = createSelector(
  selectAusData,
  selectLoanIdFromDeal,
  ({ ausSummary }, loanId) => (loanId ? (ausSummary?.[loanId] ?? null) : null),
);

export const selectIsAusSwitching = createSelector(
  selectAusData,
  ({ isAusSwitching }) => isAusSwitching,
);

export const selectIsAusSummaryExist = createSelector(selectAusSummary, ausSummary => !!ausSummary);

// SETTLED (succeeded or failed) — NOT "loaded". Callers can tell "no AUS run" apart from "not
// fetched yet", and a failed fetch still settles so nothing waits on it forever.
export const selectIsAusSummarySettled = createSelector(
  selectAusData,
  selectLoanIdFromDeal,
  ({ isAusSummarySettled }, loanId) => !!(loanId && isAusSummarySettled?.[loanId]),
);

export const selectIsAusApproveEligible = createSelector(
  selectAusSummary,
  ausSummary =>
    !!(ausSummary?.duSummary?.approveEligibility || ausSummary?.lpSummary?.approveEligibility),
);

// Risk-approved regardless of eligibility — reads the raw AUS Recommendation extraction
// value ("Approve/Eligible" OR "Approve/Ineligible" for DU, "Accept" for LP). A finding is
// also risk-approved whenever it is approve/eligible (that strictly implies approved), which
// covers loans whose approveEligibility bool is set but whose recommendation string is
// non-standard/absent.
const isRiskApprovedFinding = summary =>
  summary?.approveEligibility === true ||
  ['approve', 'accept'].some(prefix =>
    (summary?.recommendation ?? '').trim().toLowerCase().startsWith(prefix),
  );

export const selectIsAusApproved = createSelector(
  selectAusSummary,
  ausSummary =>
    isRiskApprovedFinding(ausSummary?.duSummary) || isRiskApprovedFinding(ausSummary?.lpSummary),
);

// The working casefile-intake IDs — resolved PER FIELD so each leg falls back independently to
// the latest operation's PERSISTED value when the working intake lacks it. An in-session typed
// value is always authoritative (the submit thunk writes du and lp together, so a partially-
// filled intake only arises from the DU-only MISMO prefill written at load), so only the leg the
// working intake is missing is rehydrated from the operation — which fixes the ticket's scenario:
// a typed LPA key lost on reload is restored from the errored op even though a MISMO DU prefill
// made casefileIntake non-empty. A whole-object guard would have returned the DU-only prefill and
// left lpAusKey null. Op-rehydrated values are NOT MISMO provenance — the "From MISMO import"
// chip stays gated on the separate mismoPrefill slice, never on these.
export const selectAusCasefileIntake = createSelector(
  selectAusData,
  selectLatestAusSubmitOperation,
  ({ casefileIntake }, latestOperation) => {
    const intake = casefileIntake ?? { duCasefileId: null, lpAusKey: null };

    return {
      duCasefileId: intake.duCasefileId ?? latestOperation?.duCasefileId ?? null,
      lpAusKey: intake.lpAusKey ?? latestOperation?.lpAusKey ?? null,
    };
  },
);

// Only the IDs detected in the MISMO import — the provenance source the "From MISMO import"
// chip gates on, so a user-typed/submitted ID never gets mislabeled as imported.
export const selectAusMismoPrefill = createSelector(
  selectAusData,
  ({ mismoPrefill }) => mismoPrefill ?? { duCasefileId: null, lpAusKey: null },
);

const selectAusFindingsListByAusType = typeOfAus =>
  createSelector(selectAusData, ({ findingsList }) => findingsList?.[typeOfAus] ?? []);

export const selectAusFindingsListOptionsByAusType = _.memoize(typeOfAus =>
  createSelector(selectAusFindingsListByAusType(typeOfAus), ausFindingsList =>
    formatAusFindingsOptions(ausFindingsList),
  ),
);
